import { useCallback, useEffect, useRef, useState } from 'react';
import type { Database } from 'firebase/database';
import { fetchFirebaseToken } from './api';
import { connectFirebase, signOutFirebase } from './firebase';
import type { FirebaseTokenResponse } from './types';

interface State {
  ready: boolean;
  db: Database | null;
  token: FirebaseTokenResponse | null;
  error: Error | null;
  refresh: () => Promise<void>;
}

const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000;

export function useFirebaseChat(enabled: boolean): State {
  const [state, setState] = useState<Omit<State, 'refresh'>>({
    ready: false,
    db: null,
    token: null,
    error: null,
  });
  const refreshTimer = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const connectRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const connect = useCallback(async () => {
    if (!enabled) return;
    try {
      const token = await fetchFirebaseToken();
      const { database } = await connectFirebase(token);
      if (cancelledRef.current) return;
      setState({ ready: true, db: database, token, error: null });
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      const delay = Math.max(60_000, token.expires_in_seconds * 1000 - REFRESH_BEFORE_EXPIRY_MS);
      refreshTimer.current = window.setTimeout(() => void connectRef.current(), delay);
    } catch (err) {
      if (!cancelledRef.current) {
        setState({ ready: false, db: null, token: null, error: err as Error });
      }
    }
  }, [enabled]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;
    const startTimer = window.setTimeout(() => void connect(), 0);
    return () => {
      window.clearTimeout(startTimer);
      cancelledRef.current = true;
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      void signOutFirebase();
    };
  }, [enabled, connect]);

  return { ...state, refresh: connect };
}
